import { createFileRoute, Link } from "@tanstack/react-router";
import {
  Activity,
  Archive,
  ArrowLeft,
  ArrowUpRight,
  Calendar,
  Download,
  Info,
  Loader2,
  MessageSquareText,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { SetupCard } from "@/components/setups/setup-card";
import { SetupFileRow } from "@/components/setups/setup-file-row";
import { SetupMetadata } from "@/components/setups/setup-metadata";
import { VersionSelector } from "@/components/setups/version-selector";
import { TrackLayoutImage, TrackWorldMap } from "@/components/track/track-location-panel";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useProfile } from "@/hooks/use-profile";
import { useRelatedSetups, useSetupDetail } from "@/hooks/use-setups";
import { useSubscription } from "@/hooks/use-subscription";
import { useSetupsWithTelemetry } from "@/hooks/use-telemetry";
import { analytics } from "@/lib/analytics";
import { canDownloadFile } from "@/lib/feature-access";
import type { SetupFile, SetupListItem, SetupType, SetupVersion } from "@/lib/types";
import { generateSetupZip } from "@/lib/zip";

export const Route = createFileRoute("/_auth/app/setups/$setupId")({
  component: SetupDetailPage,
});

/** Group setup files by setup_type, with a separate group for companion files */
function groupFiles(files: SetupFile[]) {
  const setupFiles = new Map<SetupType, SetupFile[]>();
  const companionFiles: SetupFile[] = [];

  for (const file of files) {
    if (file.file_type === "setup" && file.setup_type) {
      const group = setupFiles.get(file.setup_type) ?? [];
      group.push(file);
      setupFiles.set(file.setup_type, group);
    } else {
      companionFiles.push(file);
    }
  }

  // Order setup types consistently
  const typeOrder: SetupType[] = [
    "race",
    "race_safe",
    "qualifying",
    "qualifying_safe",
    "endurance",
    "endurance_safe",
    "wet",
    "safe",
  ];
  const orderedSetupGroups = typeOrder
    .filter((t) => setupFiles.has(t))
    .map((t) => ({ type: t, files: setupFiles.get(t)! }));

  return { setupGroups: orderedSetupGroups, companionFiles };
}

function SetupDetailPage() {
  const { setupId } = Route.useParams();
  const { data: setup, isLoading } = useSetupDetail(setupId);

  // Normalize Supabase response — single FK relations may come as objects or arrays
  const normalizedSetup = useMemo(() => {
    if (!setup) return null;
    return {
      ...setup,
      car: Array.isArray(setup.car) ? setup.car[0] : setup.car,
      track: Array.isArray(setup.track) ? setup.track[0] : setup.track,
      series: Array.isArray(setup.series) ? setup.series[0] : setup.series,
      driver: Array.isArray(setup.driver) ? setup.driver[0] : setup.driver,
      season: Array.isArray(setup.season) ? setup.season[0] : setup.season,
      versions: Array.isArray(setup.versions) ? setup.versions : [],
      files: Array.isArray(setup.files) ? setup.files : [],
    };
  }, [setup]);

  const { data: relatedSetups } = useRelatedSetups(
    normalizedSetup?.id,
    normalizedSetup?.car?.id,
    normalizedSetup?.track?.id,
  );

  const { data: setupsWithTelemetry } = useSetupsWithTelemetry();

  const { data: profile } = useProfile();
  const { data: subscription } = useSubscription(profile?.id);
  const userTier = subscription?.tier ?? "free";
  const selectedSeriesIds = subscription?.selectedSeriesIds ?? [];

  // Version selection — default to latest
  const [selectedVersionNumber, setSelectedVersionNumber] = useState<number | null>(null);

  // Set default version when setup loads
  useEffect(() => {
    if (normalizedSetup && normalizedSetup.versions.length > 0 && selectedVersionNumber === null) {
      const latest = Math.max(
        ...normalizedSetup.versions.map((v: SetupVersion) => v.version_number),
      );
      setSelectedVersionNumber(latest);
    }
  }, [normalizedSetup, selectedVersionNumber]);

  // Analytics
  useEffect(() => {
    if (normalizedSetup) {
      analytics.setupViewed({
        setupId: normalizedSetup.id,
        carName: normalizedSetup.car?.name,
        trackName: normalizedSetup.track?.name,
      });
    }
  }, [normalizedSetup?.id, normalizedSetup?.car?.name, normalizedSetup]);

  // Derive selected version and its files
  const selectedVersion: SetupVersion | undefined = useMemo(() => {
    if (!normalizedSetup || selectedVersionNumber === null) return undefined;
    return normalizedSetup.versions.find(
      (v: SetupVersion) => v.version_number === selectedVersionNumber,
    );
  }, [normalizedSetup, selectedVersionNumber]);

  const versionFiles = useMemo(() => {
    if (!normalizedSetup || !selectedVersion) return { setupGroups: [], companionFiles: [] };
    const filtered = normalizedSetup.files.filter(
      (f: SetupFile) => f.version_id === selectedVersion.id,
    );
    return groupFiles(filtered);
  }, [normalizedSetup, selectedVersion]);

  // Download handlers — "all files" includes companions (.rpy/.ibt/.blap/.olap),
  // "sto only" mirrors the package-page dual-button pattern.
  const [downloading, setDownloading] = useState<"all" | "sto" | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const setupFileCount = versionFiles.setupGroups.reduce((sum, g) => sum + g.files.length, 0);
  const companionFileCount = versionFiles.companionFiles.length;
  const totalFileCount = setupFileCount + companionFileCount;

  // Check if user can download setup files (not replay/telemetry)
  const canDownloadSetups = useMemo(() => {
    if (!normalizedSetup) return { allowed: false, requiredTier: "plus" as const };
    return canDownloadFile({
      tier: userTier,
      fileType: "setup",
      tierRequired: "free",
      isFree: normalizedSetup.is_free_sample,
      seriesId: normalizedSetup.series?.id ?? null,
      selectedSeriesIds,
    });
  }, [normalizedSetup, userTier, selectedSeriesIds]);

  const canDownloadCompanions = userTier !== "free" && companionFileCount > 0;

  const isInTelemetryList = useMemo(() => {
    if (!normalizedSetup || !setupsWithTelemetry) return false;
    return setupsWithTelemetry.some((s) => s.id === normalizedSetup.id);
  }, [normalizedSetup, setupsWithTelemetry]);

  const handleDownload = useCallback(
    async (includeCompanions: boolean) => {
      if (!selectedVersion || !normalizedSetup) return;
      setDownloading(includeCompanions ? "all" : "sto");
      try {
        const result = await generateSetupZip({
          data: {
            setupId: normalizedSetup.id,
            versionId: selectedVersion.id,
            includeCompanions,
          },
        });

        // Download via signed URL (bypasses Vercel 4.5MB body limit)
        const a = document.createElement("a");
        a.href = result.downloadUrl;
        a.download = result.filename;
        a.click();
        const car = Array.isArray(normalizedSetup.car)
          ? normalizedSetup.car[0]
          : normalizedSetup.car;
        const track = Array.isArray(normalizedSetup.track)
          ? normalizedSetup.track[0]
          : normalizedSetup.track;
        analytics.setupZipDownloaded({
          setupId: normalizedSetup.id,
          carName: car?.name ?? "",
          trackName: track?.name ?? "",
        });
        toast.success("Download started");
      } catch (err: any) {
        toast.error(err.message ?? "Download failed");
      } finally {
        setDownloading(null);
      }
    },
    [selectedVersion, normalizedSetup],
  );

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Back button skeleton */}
        <div className="h-5 w-24 animate-pulse rounded bg-muted" />
        {/* Header skeleton */}
        <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
          <div className="aspect-[21/9] animate-pulse bg-muted" />
          <div className="space-y-3 p-6">
            <div className="h-7 w-3/4 animate-pulse rounded bg-muted" />
            <div className="h-5 w-1/2 animate-pulse rounded bg-muted" />
            <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
          </div>
        </div>
        {/* Files skeleton */}
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    );
  }

  // Not found
  if (!normalizedSetup) {
    return (
      <div className="flex flex-col items-center justify-center py-24">
        <h2 className="font-[Bebas_Neue] text-2xl text-foreground">Setup Not Found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          This setup may have been removed or doesn&apos;t exist.
        </p>
        <Button variant="outline" size="sm" className="mt-6" asChild>
          <Link to="/app/setups" search={{} as any}>
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            Back to Setups
          </Link>
        </Button>
      </div>
    );
  }

  const trackLabel = normalizedSetup.track?.config
    ? `${normalizedSetup.track.name} (${normalizedSetup.track.config})`
    : (normalizedSetup.track?.name ?? "");

  return (
    <div className="space-y-6">
      {/* Back link — uses browser history to preserve filters */}
      <button
        type="button"
        onClick={() => window.history.back()}
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Setups
      </button>

      {/* Header */}
      <div className="overflow-hidden rounded-xl border border-border/50 bg-card shadow-sm transition-all hover:border-border hover:shadow-md">
        {/* Car image */}
        <div className="relative aspect-[21/9] w-full overflow-hidden">
          {normalizedSetup.car?.image_url ? (
            <img
              src={normalizedSetup.car.image_url}
              alt={normalizedSetup.car.name}
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-gradient-to-br from-[#1a2744] to-[#0d1b2a]" />
          )}
          {/* Gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

          {/* Info overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <h1 className="font-[Bebas_Neue] text-3xl leading-tight text-white sm:text-4xl">
              {normalizedSetup.title}
            </h1>
            <p className="mt-1 text-sm text-white/80">
              {normalizedSetup.car?.name} &middot; {trackLabel}
            </p>
          </div>

          {/* Download count */}
          <div className="absolute top-4 right-4 flex items-center gap-1 rounded-md bg-black/60 px-2.5 py-1 text-xs text-white">
            <Download className="h-3 w-3" />
            {normalizedSetup.download_count}
          </div>
        </div>

        {/* Metadata bar */}
        <div className="flex flex-wrap items-center gap-3 border-t border-border/50 px-6 py-3">
          {/* Series badge */}
          {normalizedSetup.series && (
            <Badge variant="outline" className="border-border/50 text-xs text-foreground">
              {normalizedSetup.series.name}
            </Badge>
          )}

          {/* Season & Week */}
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="h-3.5 w-3.5" />
            {normalizedSetup.season?.name} &middot; Week {normalizedSetup.week_number}
          </span>

          {/* Free sample badge */}
          {normalizedSetup.is_free_sample && (
            <Badge className="bg-[#3B9B7A] border-transparent text-white text-[10px] px-1.5 py-0">
              Free Sample
            </Badge>
          )}

          <div className="flex-1" />

          {/* Version selector */}
          {normalizedSetup.versions.length > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Version:</span>
              <VersionSelector
                versions={normalizedSetup.versions}
                selected={selectedVersionNumber ?? normalizedSetup.version}
                onSelect={setSelectedVersionNumber}
              />
            </div>
          )}

          {/* Driver link */}
          <Link
            to="/drivers/$driverId"
            params={{ driverId: normalizedSetup.driver?.id }}
            className="flex items-center gap-2 text-sm text-primary/80 transition-colors hover:text-primary hover:underline"
          >
            {normalizedSetup.driver?.avatar_url && (
              <img
                src={normalizedSetup.driver.avatar_url}
                alt={normalizedSetup.driver.display_name}
                className="h-6 w-6 rounded-full object-cover"
              />
            )}
            {normalizedSetup.driver?.display_name}
          </Link>
        </div>
      </div>

      {/* Two-column layout: setup data + track location */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:items-start">
        {/* Left column: metadata + files */}
        <div className="flex flex-col gap-6">
          {/* Metadata grid */}
          {selectedVersion && <SetupMetadata version={selectedVersion} />}

          <Separator className="bg-border/50" />

          {/* Files section — flat list */}
          <div className="space-y-4">
            {/* Action buttons — Download All + Download .sto Only + Analyze */}
            {((setupFileCount >= 1 && canDownloadSetups.allowed) || isInTelemetryList) && (
              <div className="flex w-full flex-col items-stretch gap-2">
                {canDownloadSetups.allowed && totalFileCount >= 2 && (
                  <Button
                    className="min-w-0 flex-1"
                    onClick={() => handleDownload(true)}
                    disabled={downloading !== null || !canDownloadCompanions}
                    title={
                      !canDownloadCompanions && companionFileCount > 0
                        ? "Plus subscription required to include companion files"
                        : undefined
                    }
                  >
                    {downloading === "all" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Archive className="mr-2 h-4 w-4" />
                    )}
                    {downloading === "all"
                      ? "Preparing ZIP..."
                      : `Download All Files (${totalFileCount})`}
                  </Button>
                )}
                {canDownloadSetups.allowed && setupFileCount >= 1 && (
                  <Button
                    variant="outline"
                    className="min-w-0 flex-1"
                    onClick={() => handleDownload(false)}
                    disabled={downloading !== null}
                  >
                    {downloading === "sto" ? (
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    ) : (
                      <Download className="mr-2 h-4 w-4" />
                    )}
                    {downloading === "sto"
                      ? "Preparing ZIP..."
                      : `Download .sto Only (${setupFileCount})`}
                  </Button>
                )}
                {isInTelemetryList &&
                  (userTier === "premium" ? (
                    <Button
                      className="min-w-0 flex-1 bg-[#E8872A] text-white hover:bg-[#E8872A]/90"
                      asChild
                    >
                      <Link to="/app/telemetry" search={{ setupId: normalizedSetup.id }}>
                        <Activity className="mr-2 h-4 w-4" />
                        Analyze in Laptime Assistant
                      </Link>
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      className="min-w-0 flex-1 gap-1.5 border-[#E8872A]/50 text-[#E8872A] hover:bg-[#E8872A]/10 hover:text-[#E8872A]"
                      asChild
                    >
                      <a href="/#pricing">
                        <ArrowUpRight className="h-4 w-4" />
                        Upgrade to Premium
                      </a>
                    </Button>
                  ))}
              </div>
            )}

            <div className="space-y-1.5">
              {/* All setup files in a single flat list */}
              {versionFiles.setupGroups.flatMap((group) =>
                group.files.map((file) => (
                  <SetupFileRow
                    key={file.id}
                    file={file}
                    tier={userTier}
                    isFree={normalizedSetup.is_free_sample}
                    seriesId={normalizedSetup.series?.id ?? null}
                    selectedSeriesIds={selectedSeriesIds}
                  />
                )),
              )}

              {/* Companion files (Replay, Telemetry) */}
              {versionFiles.companionFiles.map((file) => (
                <SetupFileRow
                  key={file.id}
                  file={file}
                  tier={userTier}
                  isFree={normalizedSetup.is_free_sample}
                  seriesId={normalizedSetup.series?.id ?? null}
                  selectedSeriesIds={selectedSeriesIds}
                />
              ))}
            </div>

            {/* Empty files state */}
            {versionFiles.setupGroups.length === 0 && versionFiles.companionFiles.length === 0 && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No files available for this version.
              </p>
            )}

            {/* File types + install locations explainer */}
            {(versionFiles.setupGroups.length > 0 || versionFiles.companionFiles.length > 0) && (
              <div className="overflow-hidden rounded-lg border border-border/50 bg-card/30 text-sm transition-all hover:border-border hover:shadow-md">
                <button
                  type="button"
                  onClick={() => setHelpOpen((v) => !v)}
                  className="flex w-full items-center gap-2 px-4 py-2.5 text-left text-muted-foreground transition-colors hover:text-foreground"
                >
                  <Info className="h-4 w-4" />
                  <span>What are these files and where do they go?</span>
                  <motion.span
                    className="ml-auto text-xs text-muted-foreground/70"
                    animate={{ rotate: helpOpen ? 180 : 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                  >
                    ▾
                  </motion.span>
                </button>
                <AnimatePresence initial={false}>
                  {helpOpen && (
                    <motion.div
                      key="help-content"
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeOut" }}
                      style={{ overflow: "hidden" }}
                    >
                      <div className="space-y-3 border-t border-border/50 px-4 py-3 text-xs leading-relaxed text-muted-foreground">
                        <p>
                          Drop files into your iRacing folder (usually{" "}
                          <code className="rounded bg-muted px-1 py-0.5 text-foreground">
                            Documents/iRacing/
                          </code>
                          ). iRacing picks them up automatically the next time you load the
                          car/track.
                        </p>
                        <ul className="space-y-2">
                          <li>
                            <span className="font-medium text-foreground">.sto</span> — Car setup.
                            Place in{" "}
                            <code className="rounded bg-muted px-1 py-0.5 text-foreground">
                              Documents/iRacing/setups/&lt;car&gt;/
                            </code>
                            . Select it in-sim from the garage &rarr; Setups tab.
                          </li>
                          <li>
                            <span className="font-medium text-foreground">.rpy</span> — Replay file.
                            Place in{" "}
                            <code className="rounded bg-muted px-1 py-0.5 text-foreground">
                              Documents/iRacing/replay/
                            </code>
                            . Open via Replays menu in the iRacing UI to watch the driver&apos;s
                            lap.
                          </li>
                          <li>
                            <span className="font-medium text-foreground">.ibt</span> — Telemetry
                            (raw sensor data). Place in{" "}
                            <code className="rounded bg-muted px-1 py-0.5 text-foreground">
                              Documents/iRacing/telemetry/
                            </code>
                            . Open in tools like MoTeC i2 Pro or upload to our Lap Time Assistant
                            for AI coaching.
                          </li>
                          <li>
                            <span className="font-medium text-foreground">.blap / .olap</span> —
                            Best lap / optimal lap reference files. Same folder as telemetry; used
                            by iRacing&apos;s in-sim delta bar to compare against the reference lap.
                          </li>
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            )}
          </div>
        </div>

        {/* Right column: world map + track layout + driver notes */}
        {normalizedSetup.track && (
          <div className="hidden flex-col gap-4 lg:flex">
            <TrackWorldMap track={normalizedSetup.track} />
            <TrackLayoutImage track={normalizedSetup.track} />
            {normalizedSetup.description && (
              <div className="flex flex-col rounded-xl border border-border/50 bg-card/50 p-5 transition-all hover:border-border hover:shadow-md">
                <div className="mb-3 flex items-center gap-2">
                  <MessageSquareText className="h-4 w-4 text-muted-foreground" />
                  <h3 className="text-sm font-medium text-foreground">Driver Notes</h3>
                  {normalizedSetup.driver?.display_name && (
                    <span className="text-xs text-muted-foreground">
                      — {normalizedSetup.driver.display_name}
                    </span>
                  )}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground">
                  {normalizedSetup.description}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Related setups */}
      {relatedSetups && relatedSetups.length > 0 && (
        <>
          <Separator className="bg-border/50" />
          <div className="space-y-4">
            <h2 className="font-[Bebas_Neue] text-xl text-foreground">Related Setups</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {relatedSetups.map((related: SetupListItem) => (
                <SetupCard key={related.id} setup={related} />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
